import React, { useState } from "react";
import {
  Badge,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  IconButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import FilterListIcon from "@mui/icons-material/FilterList";
import RefreshIcon from "@mui/icons-material/Refresh";
import { MORE_FILTERS, activeMoreFilterCount } from "./historyFormat";
import { GLAZER_GREEN } from "./regmColors";
import HelpTip from "./HelpTip";
import HELP from "./helpText";
import { TOUCH, focusRing } from "./regmStyles";

const QUICK_CHIPS = [
  ["flaggedOnly", "Flagged"],
  ["pendingReview", "Pending review"],
];

// Search, quick toggle chips, "More filters" menu, refresh and counts. All state lives in HistoryTab.
const HistoryFilters = ({ query, onQueryChange, filters, onToggle, onRefresh, loading, counts }) => {
  const [menuAnchor, setMenuAnchor] = useState(null);
  const moreCount = activeMoreFilterCount(filters);

  return (
    <Stack
      direction={{ xs: "column", sm: "row" }}
      spacing={1.5}
      alignItems={{ sm: "center" }}
      useFlexGap
      flexWrap="wrap"
    >
      <TextField
        size="small"
        label="Search security, ISIN or trader"
        value={query}
        onChange={(e) => onQueryChange(e.target.value)}
        sx={{ minWidth: { sm: 280 } }}
      />
      <Stack direction="row" spacing={1} alignItems="center" useFlexGap flexWrap="wrap">
        {QUICK_CHIPS.map(([key, label]) => {
          const on = filters[key] === true;
          return (
            <Chip
              key={key}
              label={label}
              clickable
              onClick={() => onToggle(key)}
              aria-pressed={on}
              variant={on ? "filled" : "outlined"}
              sx={{
                height: TOUCH,
                borderRadius: 5,
                ...(on && { bgcolor: GLAZER_GREEN, color: "#fff", "&:hover": { bgcolor: GLAZER_GREEN } }),
                ...focusRing,
              }}
            />
          );
        })}
        <Badge
          badgeContent={moreCount}
          overlap="rectangular"
          sx={{ "& .MuiBadge-badge": { bgcolor: GLAZER_GREEN, color: "#fff" } }}
        >
          <Button
            variant="outlined"
            color="inherit"
            startIcon={<FilterListIcon />}
            onClick={(e) => setMenuAnchor(e.currentTarget)}
            aria-haspopup="menu"
            aria-expanded={Boolean(menuAnchor)}
            aria-label={moreCount ? `More filters, ${moreCount} active` : "More filters"}
            sx={{ minHeight: TOUCH, textTransform: "none", ...focusRing }}
          >
            More filters
          </Button>
        </Badge>
        <HelpTip text={HELP.moreFilters} label="more filters" />
        <Menu anchorEl={menuAnchor} open={Boolean(menuAnchor)} onClose={() => setMenuAnchor(null)}>
          {MORE_FILTERS.map(([key, label]) => {
            const on = filters[key] === true;
            return (
              <MenuItem
                key={key}
                role="menuitemcheckbox"
                aria-checked={on}
                onClick={() => onToggle(key)}
                sx={{ minHeight: TOUCH }}
              >
                <ListItemIcon>
                  <Checkbox
                    edge="start"
                    size="small"
                    checked={on}
                    tabIndex={-1}
                    disableRipple
                    inputProps={{ "aria-hidden": true }}
                  />
                </ListItemIcon>
                <ListItemText primary={label} />
              </MenuItem>
            );
          })}
        </Menu>
        <Tooltip title="Refresh records">
          <span>
            <IconButton onClick={onRefresh} disabled={loading} aria-label="Refresh records" sx={focusRing}>
              {loading ? <CircularProgress size={20} /> : <RefreshIcon />}
            </IconButton>
          </span>
        </Tooltip>
      </Stack>
      {counts && (
        <Typography variant="body2" color="text.secondary" sx={{ ml: { sm: "auto" } }}>
          {counts.shown} of {counts.total} records
        </Typography>
      )}
    </Stack>
  );
};

export default HistoryFilters;
